import type { Company, Credit, Job } from '@kanda-libs/ks-frontend-services';
import type { Payment, PaymentOption } from '@kanda-libs/ks-frontend-services';

import { URLS } from 'common/constants';
import { ORDER, SORTING } from 'components/loan/LoanTable/constants';
import get from 'lodash.get';

export interface FormattedCompany {
  companyName: string;
  companyContactEmail: string;
  companyContactPhone: string;
}

const getCompany = (cid: string, companies: Company[]): FormattedCompany => {
  if (!cid)
    return {
      companyName: '',
      companyContactEmail: '',
      companyContactPhone: '',
    };
  const filtered = companies.filter((company) => company?.id === cid);
  if (filtered?.length === 0)
    return {
      companyName: '',
      companyContactEmail: '',
      companyContactPhone: '',
    };
  const company = filtered?.[0];
  const companyName =
    company?.company_type === 'sole_trader'
      ? company?.sole_trader_info?.trading_name || ''
      : company?.limited_company_info?.company_name || '';
  return {
    companyName,
    companyContactEmail:
      company?.contact_info?.contact_email || 'No contact email',
    companyContactPhone:
      company?.contact_info?.contact_phone || 'No contact phone',
  };
};

const getJob = (kid?: string, jobs: Job[] = []) => {
  if (!kid)
    return {
      cid: null,
      archived: 'no',
      jobTitle: '',
      jobType: '',
      customerDetails: {
        firstName: 'No',
        lastName: 'name added',
        name: 'No name added',
        email: 'No email added',
        phone: 'No phone added',
        postCode: 'No postcode added',
        addressLine1: 'No line 1 added',
        addressLine2: undefined,
        city: 'No city added',
      },
    };
  const filtered = jobs.filter((job) => job?.id === kid);
  if (filtered?.length === 0)
    return {
      cid: null,
      archived: 'no',
      jobTitle: '',
      jobType: '',
      customerDetails: {
        firstName: 'No',
        lastName: 'name added',
        name: 'No name added',
        email: 'No email added',
        phone: 'No phone added',
        postCode: 'No postcode added',
        addressLine1: 'No line 1 added',
        addressLine2: undefined,
        city: 'No city added',
      },
    };
  const job = filtered?.[0];
  return {
    cid: job?.cid,
    archived: job?.archived === 'yes' ? 'archived' : 'no',
    jobTitle: job?.title || '',
    jobType: job?.job_type || 'standard',
    customerDetails: {
      firstName: job?.customer?.first_name || '',
      lastName: job?.customer?.last_name || '',
      name: `${job?.customer?.first_name || ''} ${
        job?.customer?.last_name || ''
      }`,
      email: job?.customer?.email || '',
      phone: job?.customer?.phone || '',
      postCode: job?.customer?.address?.postcode || '',
      addressLine1: job?.customer?.address?.line_1 || '',
      addressLine2: job?.customer?.address?.line_2 || '',
      city: job?.customer?.address?.city || '',
    },
  };
};

export interface FormattedLoan
  extends Partial<Omit<Job, 'archived'>>,
    Partial<Company> {
  id: string;
  companyId: string;
  jobId: string;
  ownerId: string;
  externalId: string;
  link: string;
  createdAt: Date | null | string;
  updatedAt: Date | null | string;
  loanProvider: string;
  loanStatus: string;
  loanAmount: number;
  depositAmount: number;
  totalAmount: number;
  financeOption: string;
  archived: string;
  companyName: string;
  companyContactEmail: string;
  companyContactPhone: string;
}

export const formatLoans = (
  payments: Credit[],
  jobs: Job[],
  companies: Company[],
  solar: boolean,
): FormattedLoan[] => {
  if (!payments) return [];
  return payments
    .filter((payment) => payment.flow_type !== 'example')
    .reduce((loans, payment) => {
      const job = getJob(payment?.kid, jobs);
      if (!job?.cid) return loans;
      const jobIsSolar = job?.jobType === 'solar';

      if (solar) {
        if (!jobIsSolar) return loans;
      } else {
        if (jobIsSolar) return loans;
      }

      const company = getCompany(job?.cid, companies);
      loans.push({
        id: payment?.id || '',
        xid: payment?.xid || '',
        companyId: job?.cid || '',
        jobId: payment?.kid || '',
        ownerId: payment?.oid || '',
        externalId: payment?.xid || '',
        link: URLS.PAYMENT.replace('{id}', payment?.id || ''),
        createdAt: payment?.metadata?.created_at || '',
        updatedAt: payment?.metadata?.updated_at || '',
        loanProvider: payment?.provider || '',
        loanStatus: payment?.state || '',
        loanAmount: payment?.amount?.amount || 0,
        depositAmount: payment?.deposit_value?.amount || 0,
        totalAmount:
          (payment?.amount?.amount || 0) +
          (payment?.deposit_value?.amount || 0),
        financeOption: payment?.finance_option?.name,
        ...job,
        ...company,
      });
      return loans;
    }, [] as FormattedLoan[]);
};

/**
 * Sorts loan by price
 */
const sortByName = (data: FormattedLoan[], accessor: string, order: string) =>
  data.sort((data1, data2) => {
    const name1 = get(data1, accessor);
    const name2 = get(data2, accessor);
    if (order === ORDER.INVERSE) return name2.localeCompare(name1);
    return name1.localeCompare(name2);
  });

/**
 * Sorts loan by price
 */
const sortByPrice = (
  data: FormattedLoan[],
  accessor: string,
  order: string,
): FormattedLoan[] =>
  data.sort((data1, data2) => {
    const price1 = get(data1, accessor);
    const price2 = get(data2, accessor);
    if (order === ORDER.INVERSE) return price2 - price1;
    return price1 - price2;
  });

/**
 * Sorts loan by updated at date
 */
const sortByUpdatedAt = (loans: FormattedLoan[], order: string) =>
  loans.sort((loan1, loan2) => {
    const loan1Date = loan1.updatedAt as unknown as number;
    const loan2Date = loan2.updatedAt as unknown as number;

    if (order === ORDER.INVERSE)
      return (
        (new Date(loan1Date) as unknown as number) -
        (new Date(loan2Date) as unknown as number)
      );
    return (
      (new Date(loan2Date) as unknown as number) -
      (new Date(loan1Date) as unknown as number)
    );
  });

export const sortData = (
  data: FormattedLoan[],
  sorting: string,
  order: string,
): FormattedLoan[] => {
  if (!data) return [];
  // If no jobs, return null to show placeholder
  if (data.length === 0) return [];

  // If sorting is updated_at, return data sorted by updated at date
  if (sorting === SORTING.PRICE) return sortByPrice(data, 'totalAmount', order);
  // If sorting is updated_at, return data sorted by updated at date
  if (sorting === SORTING.ID) return sortByName(data, 'id', order);
  // If sorting is updated_at, return data sorted by updated at date
  if (sorting === SORTING.LOAN) return sortByPrice(data, 'loanAmount', order);
  // If sorting is updated_at, return data sorted by updated at date
  if (sorting === SORTING.DEPOSIT)
    return sortByPrice(data, 'depositAmount', order);
  // If sorting is updated_at, return data sorted by updated at date
  if (sorting === SORTING.JOB_TITLE) return sortByName(data, 'jobTitle', order);
  // If sorting is updated_at, return data sorted by updated at date
  if (sorting === SORTING.JOB_ID) return sortByName(data, 'jobId', order);
  // If sorting is updated_at, return data sorted by updated at date
  if (sorting === SORTING.CUSTOMER)
    return sortByName(data, 'customerDetails.name', order);
  // If sorting is updated_at, return data sorted by updated at date
  if (sorting === SORTING.COMPANY)
    return sortByName(data, 'companyDetails.name', order);
  // If sorting is updated_at, return data sorted by updated at date
  if (sorting === SORTING.UPDATED_AT) return sortByUpdatedAt(data, order);

  return data;
};

export interface FormattedPayment {
  id?: string;
  jobId?: string;
  status?: string;
  method?: PaymentOption['payment_method'];
  total?: number;
  updatedAt?: Date | string;
  createdAt?: Date | string;
}

export const formatPayment = (payment?: Payment): FormattedPayment => {
  if (!payment) return {};

  const id = payment?.id || '';
  const jobId = payment?.kid || '';
  const status = payment?.status || 'unpaid';
  const method = payment?.payment_option?.payment_method || 'cash';
  const total = payment?.payment_option?.amount?.amount || 0;
  const updatedAt = payment?.metadata?.updated_at || '';
  const createdAt = payment?.metadata?.created_at || '';

  return {
    id,
    jobId,
    status,
    method,
    total,
    updatedAt,
    createdAt,
  };
};
